import React, { useEffect, useMemo, useState } from "react";
import { PackageCheck, ClipboardList, CheckCircle2, X, ArrowRight, Layers, Tag, Warehouse, RotateCcw, AlertTriangle, ShoppingCart, Info, Search, Receipt, Pencil } from "lucide-react";
import { toast } from "sonner";
import {
  api,
  formatApiError,
  fmtDate,
  fmtIDR,
  openPdf,
  wibDateISO,
  wibDaysAgo,
} from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, Panel, StatusBadge } from "../../lib/ui";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import {
  filterProductionOrders,
  ProductionTableControls,
  ProductionTablePagination,
} from "../../components/production/ProductionTableControls";
import { ProductionReceiptHistory } from "../../components/production/ProductionReceiptHistory";

const RECEIVABLE = ["Dikirim ke Konveksi", "Sedang Dikerjakan", "Diterima Sebagian"];
const num = (n) => new Intl.NumberFormat("id-ID").format(Math.round((Number(n) || 0) * 100) / 100);
const orderStatusOf = (cum, target) => cum <= 0 ? "Dikirim ke Konveksi" : (cum < target ? "Diterima Sebagian" : (cum === target ? "Diterima Selesai" : "Kelebihan Penerimaan"));
const isFullyReceived = (order) => {
  const target = Number(order.total_send || 0);
  const received = Number(order.total_received || 0);
  return target > 0 && received >= target;
};
const PAGE_SIZE = 10;

export default function ProductionReceiving() {
  const { can, canEdit, user } = useAuth();
  const write = can("production_admin");
  const canCorrectReceipt = can("production_admin", "warehouse_admin") || canEdit(user, "prod_receipt_correction");
  const [list, setList] = useState([]);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [wizOid, setWizOid] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState(wibDaysAgo(30));
  const [dateTo, setDateTo] = useState(wibDateISO());
  const [page, setPage] = useState(1);
  const load = () => api.get("/production/orders").then((response) => {
    setList(response.data);
    setHistoryRefreshKey((value) => value + 1);
  });
  useEffect(() => { load(); }, []);
  const receivable = list.filter((o) => RECEIVABLE.includes(o.status));
  const filteredReceivable = useMemo(() => filterProductionOrders({
    orders: receivable,
    search,
    status: statusFilter,
    dateFrom,
    dateTo,
  }), [receivable, search, statusFilter, dateFrom, dateTo]);
  const totalPages = Math.max(1, Math.ceil(filteredReceivable.length / PAGE_SIZE));
  const pageItems = filteredReceivable.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const resetFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setDateFrom(wibDaysAgo(30));
    setDateTo(wibDateISO());
    setPage(1);
  };
  const updatePage = (callback) => (value) => {
    callback(value);
    setPage(1);
  };
  const completeOrder = async (order) => {
    try {
      await api.post(`/production/orders/${order.id}/complete`);
      toast.success(`${order.number} ditandai Selesai`);
      load();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    }
  };

  return (
    <div data-testid="receiving-page">
      <PageHeader title="Penerimaan Produksi" subtitle="Receive finished goods from tailors and post them to warehouse stock." />
      <div className="font-semibold text-cocoa mb-1.5 flex items-center gap-1.5"><ClipboardList className="w-4 h-4" />Order Siap Diterima</div>
      <ProductionTableControls
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={updatePage(setDateFrom)}
        onDateToChange={updatePage(setDateTo)}
        onReset={resetFilters}
        onSearchChange={updatePage(setSearch)}
        onStatusChange={updatePage(setStatusFilter)}
        resultCount={filteredReceivable.length}
        search={search}
        status={statusFilter}
        statusOptions={RECEIVABLE}
        testIdPrefix="receiving"
        totalCount={receivable.length}
      />
      <div className="space-y-3 md:hidden" data-testid="receiving-mobile-order-list">
        {pageItems.map((o) => {
          const send = Number(o.total_send || 0);
          const received = Number(o.total_received || 0);
          const remaining = Math.max(send - received, 0);
          const productNames = o.product_names || [o.product_name];
          const readyToComplete = isFullyReceived(o);
          return (
            <div
              key={o.id}
              className="border border-taupe/25 rounded-lg p-3 space-y-3"
              data-testid={`recv-mobile-row-${o.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-mono text-xs text-wmuted">{o.number}</div>
                  <div className="font-medium text-cocoa mt-1" data-testid={`recv-mobile-products-${o.id}`}>
                    {productNames.map((name) => (
                      <div key={name}>{name}</div>
                    ))}
                  </div>
                  <div className="text-xs text-wmuted mt-1">{o.partner_name}</div>
                  <div className="text-xs text-wmuted mt-1">Dibuat {fmtDate(o.created_at)}</div>
                </div>
                <StatusBadge status={o.status} />
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div>
                  <div className="text-wmuted">Target</div>
                  <div className="font-semibold text-cocoa">{send}</div>
                </div>
                <div>
                  <div className="text-wmuted">Diterima</div>
                  <div className="font-semibold text-cocoa">{received}</div>
                </div>
                <div>
                  <div className="text-wmuted">Sisa</div>
                  <div className="font-semibold text-cocoa">{remaining}</div>
                </div>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span
                  className={`text-[11px] px-1.5 py-0.5 rounded ${o.migration_validation?.status === "Perlu Tinjau" ? "bg-amber-100 text-amber-700" : "bg-status-passed/15 text-status-passed"}`}
                  data-testid={`recv-mobile-validation-${o.id}`}
                >
                  {o.migration_validation
                    ? `${o.migration_validation.mapped_product_count} Produk · ${o.migration_validation.status}`
                    : "Data reguler"}
                </span>
                <div className="flex gap-1.5">
                  {received > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openPdf(`/production/orders/${o.id}/sewing-invoice.pdf`, `Nota-Harga-Jahit-${o.number}.pdf`)}
                      data-testid={`recv-mobile-nota-${o.id}`}
                      className="border-cocoa text-cocoa h-8 px-2"
                    >
                      <Receipt className="w-4 h-4" />
                    </Button>
                  )}
                  {write && readyToComplete ? (
                    <Button
                      size="sm"
                      onClick={() => completeOrder(o)}
                      data-testid={`recv-mobile-complete-${o.id}`}
                      className="bg-status-passed text-white h-8"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Selesai
                    </Button>
                  ) : write && (
                    <Button
                      size="sm"
                      onClick={() => setWizOid(o.id)}
                      data-testid={`recv-mobile-btn-${o.id}`}
                      className="bg-status-passed text-white h-8"
                    >
                      <PackageCheck className="w-4 h-4 mr-1" />
                      Terima
                    </Button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {!pageItems.length && (
          <div className="text-center text-wmuted py-10" data-testid="receiving-mobile-empty-state">
            <PackageCheck className="w-6 h-6 mx-auto mb-2 opacity-50" />
            Belum ada order konveksi yang siap diterima.
          </div>
        )}
      </div>
      <Panel className="hidden md:block">
        <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">
            <TableHead>No Order</TableHead><TableHead>Konveksi</TableHead><TableHead>Produk</TableHead>
            <TableHead>Validasi</TableHead><TableHead className="text-right">Target</TableHead><TableHead className="text-right">Diterima</TableHead>
            <TableHead className="text-right">Sisa</TableHead><TableHead>Progres</TableHead><TableHead>Created At</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {pageItems.map((o) => {
              const send = Number(o.total_send || 0), recv = Number(o.total_received || 0);
              const pct = send > 0 ? Math.min(100, Math.round((recv / send) * 100)) : 0;
              const readyToComplete = isFullyReceived(o);
              return (
                <TableRow key={o.id} data-testid={`recv-row-${o.id}`}>
                  <TableCell className="font-mono text-xs">{o.number}</TableCell>
                  <TableCell>{o.partner_name}</TableCell>
                  <TableCell className="font-medium text-cocoa" data-testid={`recv-products-${o.id}`}>
                    {(o.product_names || [o.product_name]).map((name) => (
                      <div key={name}>{name}</div>
                    ))}
                  </TableCell>
                  <TableCell data-testid={`recv-validation-${o.id}`}>
                    {o.migration_validation ? (
                      <span className={`text-[11px] px-1.5 py-0.5 rounded ${o.migration_validation.status === "Valid" ? "bg-status-passed/15 text-status-passed" : "bg-amber-100 text-amber-700"}`}>
                        {o.migration_validation.mapped_product_count} Produk · {o.migration_validation.status}
                      </span>
                    ) : "—"}
                  </TableCell>
                  <TableCell className="text-right">{send}</TableCell>
                  <TableCell className="text-right font-semibold">{recv}</TableCell>
                  <TableCell className="text-right">{Math.max(send - recv, 0)}</TableCell>
                  <TableCell><div className="flex items-center gap-2"><div className="w-20 h-1.5 rounded-full bg-taupe/20 overflow-hidden"><div className="h-full bg-status-passed" style={{ width: `${pct}%` }} /></div><span className="text-xs text-wmuted">{pct}%</span></div></TableCell>
                  <TableCell data-testid={`recv-created-at-${o.id}`}>{fmtDate(o.created_at)}</TableCell>
                  <TableCell><StatusBadge status={o.status} /></TableCell>
                  <TableCell className="text-right space-x-1 whitespace-nowrap">
                    {recv > 0 && <Button size="sm" variant="outline" onClick={() => openPdf(`/production/orders/${o.id}/sewing-invoice.pdf`, `Nota-Harga-Jahit-${o.number}.pdf`)} data-testid={`recv-nota-${o.id}`} className="border-cocoa text-cocoa h-8"><Receipt className="w-4 h-4 mr-1" />Nota Harga Jahit</Button>}
                    {write && readyToComplete ? (
                      <Button
                        size="sm"
                        onClick={() => completeOrder(o)}
                        data-testid={`recv-complete-${o.id}`}
                        className="bg-status-passed text-white"
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        Selesaikan Order
                      </Button>
                    ) : write && (
                      <Button
                        size="sm"
                        onClick={() => setWizOid(o.id)}
                        data-testid={`recv-btn-${o.id}`}
                        className="bg-status-passed text-white"
                      >
                        <PackageCheck className="w-4 h-4 mr-1" />
                        {recv > 0 ? "Lanjutkan Penerimaan" : "Terima Produksi"}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {!pageItems.length && <TableRow><TableCell colSpan={11} className="text-center text-wmuted py-10"><PackageCheck className="w-6 h-6 mx-auto mb-2 opacity-50" />Tidak ada order yang sesuai filter.</TableCell></TableRow>}
          </TableBody>
        </Table>
        </div>
      </Panel>
      <ProductionTablePagination
        onPageChange={setPage}
        page={page}
        testIdPrefix="receiving"
        totalPages={totalPages}
      />
      <ProductionReceiptHistory
        canCorrect={canCorrectReceipt}
        refreshKey={historyRefreshKey}
      />
      {wizOid && <ReceivingWizard oid={wizOid} canOverride={write} onClose={() => setWizOid(null)} onDone={load} />}
    </div>
  );
}

function ReceivingWizard({ oid, canOverride, onClose, onDone }) {
  const [ctx, setCtx] = useState(null);
  const [step, setStep] = useState(1);
  const [rows, setRows] = useState([]);
  const [wh, setWh] = useState("");
  const [notes, setNotes] = useState("");
  const [override, setOverride] = useState("");
  const [bulk, setBulk] = useState("");
  const [overwrite, setOverwrite] = useState(false);
  const [additional, setAdditional] = useState(0);
  const [reason, setReason] = useState("");
  const [approved, setApproved] = useState(false);
  const [rid, setRid] = useState(null);
  const [busy, setBusy] = useState(false);
  const [posted, setPosted] = useState(false);
  const [q, setQ] = useState("");
  const [onlyChanged, setOnlyChanged] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [finalWithDifference, setFinalWithDifference] = useState(false);
  const [finalDifferenceReason, setFinalDifferenceReason] = useState("");
  const [receipts, setReceipts] = useState([]);
  const [fullEdit, setFullEdit] = useState(false);

  useEffect(() => {
    api.get(`/production/orders/${oid}/receiving-context`).then((r) => {
      const c = r.data; setCtx(c); setWh(c.default_warehouse_id || c.order.warehouse_id || (c.warehouses[0] || {}).id || "");
      const draft = c.draft; const dItems = {}; (draft?.items || []).forEach((it) => { dItems[it.variant_id] = it; });
      setRows(c.variants.map((v) => { const di = dItems[v.variant_id]; return {
        ...v, today: di ? di.current_received_qty : 0,
        sewing_new: di ? di.sewing_price_new : v.sewing_price_previous,
        update_hpp: di ? di.update_hpp : false,
      }; }));
      if (draft) { setRid(draft.id); setNotes(draft.notes || ""); }
      const dAdd = (draft?.items || [])[0]?.additional_cost;
      setAdditional(dAdd !== undefined ? dAdd : (c.cutting_cost_per_pcs || 0));
      api.get(`/production/orders/${oid}/receipts`).then((rr) => setReceipts(rr.data)).catch(() => {});
    }).catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); onClose(); });
  }, [oid]);

  const totalTarget = ctx?.total_target || 0;
  const totalPrev = ctx?.total_previous_received || 0;
  const todayTotal = rows.reduce((s, r) => s + (parseInt(r.today) || 0), 0);
  const cumTotal = totalPrev + todayTotal;
  const remainingOrder = Math.max(0, totalTarget - cumTotal);
  const pct = totalTarget > 0 ? Math.min(100, Math.round((cumTotal / totalTarget) * 100)) : 0;
  const activeRows = rows.filter((r) => (parseInt(r.today) || 0) > 0);
  const calcHpp = (r) => r.bom_complete ? Math.round((r.material_accessory_cost + (parseFloat(r.sewing_new) || 0) + (parseFloat(additional) || 0)) * 100) / 100 : 0;
  const changedHpp = activeRows.filter((r) => r.update_hpp && Math.abs(calcHpp(r) - r.previous_hpp) > 0.5);
  const anyBomIncomplete = activeRows.some((r) => !r.bom_complete);

  const setRow = (vid, patch) => setRows((rs) => rs.map((r) => r.variant_id === vid ? { ...r, ...patch } : r));
  const fillRemaining = () => setRows((rs) => rs.map((r) => ({ ...r, today: r.remaining_qty })));
  const clearAll = () => setRows((rs) => rs.map((r) => ({ ...r, today: 0 })));
  const applyBulk = () => { const v = parseFloat(bulk) || 0; if (v <= 0) return; setRows((rs) => rs.map((r) => (overwrite || !(parseFloat(r.sewing_new) > 0) || parseFloat(r.sewing_new) === r.sewing_price_previous) ? { ...r, sewing_new: v } : r)); };

  const payload = () => ({
    warehouse_id: wh, notes, override_reason: override, hpp_approved: approved, finalize_with_difference: finalWithDifference, final_difference_reason: finalDifferenceReason,
    items: activeRows.map((r) => ({ variant_id: r.variant_id, product_id: r.product_id, product_name: r.product_name, color: r.color, size: r.size, order_qty: r.order_qty, current_received_qty: parseInt(r.today) || 0, sewing_price_new: parseFloat(r.sewing_new) || 0, additional_cost: parseFloat(additional) || 0, update_hpp: !!r.update_hpp, hpp_change_reason: reason })),
  });

  const saveDraft = async (silent) => {
    try { const r = await api.post(`/production/orders/${oid}/receipts/draft`, payload()); setRid(r.data.receipt_id); if (!silent) toast.success("Draft penerimaan disimpan"); return r.data.receipt_id; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return null; }
  };

  const goStep = async (s) => {
    if (s === 2 && step === 1) {
      if (!activeRows.length) return toast.error("Minimal satu varian harus diterima > 0");
      if (rows.some((r) => (parseInt(r.today) || 0) > r.remaining_qty) && !override && !canOverride) return toast.error("Ada penerimaan melebihi sisa. Hanya user berwenang & wajib alasan.");
      if (rows.some((r) => (parseInt(r.today) || 0) > r.remaining_qty) && !override) return toast.error("Penerimaan melebihi sisa — isi alasan override di bawah tabel.");
    }
    if (s === 3 && step === 2) { if (activeRows.some((r) => !(parseFloat(r.sewing_new) > 0))) return toast.error("Harga jahit wajib diisi untuk semua varian yang diterima"); }
    await saveDraft(true); setStep(s);
  };

  const goPosting = async () => {
    if (!activeRows.length) return toast.error("Minimal satu varian harus diterima > 0");
    if (rows.some((r) => (parseInt(r.today) || 0) > r.remaining_qty) && !override) return toast.error("Penerimaan melebihi sisa — isi alasan override di bawah tabel.");
    await saveDraft(true); setStep(4);
  };

  const doPost = async () => {
    setBusy(true);
    try {
      const id = rid || await saveDraft(true);
      if (!id) { setBusy(false); return; }
      const r = await api.post(`/production/receipts/${id}/post`, payload());
      toast.success(r.data.already_posted ? "Penerimaan sudah diposting sebelumnya" : `Penerimaan ${r.data.receipt_number || ""} berhasil diposting`);
      setPosted(true); setConfirm(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!ctx) return <Dialog open onOpenChange={onClose}><DialogContent><div className="py-10 text-center text-wmuted">Memuat...</div></DialogContent></Dialog>;
  const o = ctx.order;
  const isContinuation = (ctx.next_sequence || 1) > 1;
  const simple = true;
  const STEPS = [[1, "Receive Goods"], [4, "Confirm & Post Stock"]];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto p-0" data-testid="receiving-wizard">
        <div className="sticky top-0 z-20 bg-white border-b border-taupe/20 px-6 pt-5 pb-3">
          <div className="flex items-start justify-between">
            <div><DialogTitle className="font-heading text-2xl text-cocoa">Penerimaan Produksi</DialogTitle>
              <DialogDescription className="text-xs text-wmuted mt-0.5">{o.number} • {(o.product_names || [o.product_name]).join(" + ")} • {o.partner_name}</DialogDescription></div>
            <button onClick={onClose} className="text-wmuted hover:text-cocoa" data-testid="wizard-close"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex items-center gap-1 mt-3 text-xs">
            {STEPS.map(([n, label], i) => { return (
              <React.Fragment key={n}>
                <div className={`flex items-center gap-1.5 ${step >= n ? "text-cocoa" : "text-wmuted"}`}>
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold ${step > n ? "bg-status-passed text-white" : step === n ? "bg-cocoa text-ivory" : "bg-taupe/20 text-wmuted"}`}>{step > n ? "✓" : i + 1}</span>
                  <span className="font-medium hidden sm:inline">{i + 1}. {label}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-1 ${step > n ? "bg-status-passed" : "bg-taupe/25"}`} />}
              </React.Fragment>
            ); })}
          </div>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* HEADER SUMMARY */}
          <div className="flex flex-wrap items-center gap-4 bg-ivory/60 border border-taupe/25 rounded-xl p-3" data-testid="wizard-summary">
            <div><div className="text-[11px] text-wmuted">Target Order</div><div className="text-lg font-bold text-cocoa">{totalTarget} <span className="text-xs font-normal text-wmuted">pcs</span></div></div>
            <div className="w-px h-9 bg-taupe/25" />
            <div><div className="text-[11px] text-wmuted">Sudah Diterima Sebelumnya</div><div className="text-lg font-bold text-cocoa">{totalPrev} <span className="text-xs font-normal text-wmuted">pcs</span></div></div>
            <div className="w-px h-9 bg-taupe/25" />
            <div><div className="text-[11px] text-wmuted">Penerimaan Hari Ini</div><div className="text-lg font-bold text-status-passed" data-testid="sum-today">{todayTotal} <span className="text-xs font-normal text-wmuted">pcs</span></div></div>
            <div className="w-px h-9 bg-taupe/25" />
            <div><div className="text-[11px] text-wmuted">Sisa Belum Diterima</div><div className="text-lg font-bold text-cocoa">{remainingOrder} <span className="text-xs font-normal text-wmuted">pcs</span></div></div>
            <div className="flex-1 min-w-[160px]"><div className="text-[11px] text-wmuted mb-1">{cumTotal} dari {totalTarget} pcs • {pct}%</div><div className="h-2 rounded-full bg-taupe/20 overflow-hidden"><div className="h-full bg-status-passed transition-all" style={{ width: `${pct}%` }} /></div></div>
            <span className="text-[11px] px-2.5 py-1 rounded-full border border-amber-300 text-amber-700 bg-amber-50 flex items-center gap-1" data-testid="order-status-badge"><Info className="w-3.5 h-3.5" />{orderStatusOf(cumTotal, totalTarget)}</span>
          </div>

          {posted ? (
            <div className="py-8 text-center space-y-2" data-testid="wizard-posted"><CheckCircle2 className="w-14 h-14 text-status-passed mx-auto" /><div className="text-lg font-heading font-bold text-cocoa">Penerimaan diposting</div><div className="text-sm text-wmuted">Stok, purchase batch & HPP telah diperbarui.</div><Button onClick={onClose} className="bg-cocoa text-ivory mt-2">Tutup</Button></div>
          ) : (<>
          {/* STEP 1 */}
          {step === 1 && <div data-testid="step-1">
            {ctx.migration_validation?.status === "Perlu Tinjau" && <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800" data-testid="migration-validation-warning"><div className="font-semibold">Data migrasi perlu ditinjau sebelum penerimaan</div><div className="mt-1">Varian sumber: {ctx.migration_validation.source_variant_count} · varian terbaca: {ctx.migration_validation.mapped_variant_count} · belum cocok: {ctx.migration_validation.unmatched_variant_count}</div></div>}
            <div className="flex flex-wrap gap-2 mb-2">
              <Button size="sm" variant="outline" className="border-taupe text-cocoa" onClick={fillRemaining} data-testid="fill-remaining">Isi Semua Sesuai Sisa</Button>
              <Button size="sm" variant="outline" className="border-taupe text-cocoa" onClick={clearAll} data-testid="clear-all">Kosongkan</Button>
            </div>
            <div className="space-y-2 md:hidden" data-testid="receiving-mobile-variant-list">
              {rows.map((r) => {
                const today = parseInt(r.today) || 0;
                const cumulative = r.previous_received_qty + today;
                const status = orderStatusOf(cumulative, r.order_qty);
                return (
                  <div
                    key={r.variant_id || `${r.product_id}-${r.size}`}
                    className="border border-taupe/20 rounded-lg p-3"
                    data-testid={`s1-mobile-row-${r.variant_id}`}
                  >
                    <div className="text-xs text-wmuted" data-testid={`s1-mobile-product-${r.variant_id}`}>
                      {r.product_name}
                    </div>
                    <div className="font-medium text-cocoa mt-0.5">
                      {r.color} / {r.size}
                    </div>
                    {!r.variant_exists && (
                      <div className="text-[10px] text-status-reject mt-1">
                        Varian belum ada di master produk
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                      <div>
                        <div className="text-wmuted">Target</div>
                        <div className="font-semibold text-cocoa">{r.order_qty}</div>
                      </div>
                      <div>
                        <div className="text-wmuted">Sebelumnya</div>
                        <div className="font-semibold text-cocoa">{r.previous_received_qty}</div>
                      </div>
                      <div>
                        <div className="text-wmuted">Sisa</div>
                        <div className="font-semibold text-cocoa">{r.remaining_qty}</div>
                      </div>
                    </div>
                    <div className="flex items-end justify-between gap-3 mt-3">
                      <div>
                        <Label className="text-xs text-wmuted">Diterima hari ini</Label>
                        <Input
                          type="number"
                          min="0"
                          value={r.today}
                          onChange={(e) => setRow(r.variant_id, { today: e.target.value })}
                          className={`h-9 w-24 mt-1 text-center ${today > r.remaining_qty ? "border-amber-400" : ""}`}
                          data-testid={`s1-mobile-today-${r.variant_id}`}
                        />
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-wmuted">Total setelah</div>
                        <div className="font-semibold text-cocoa">{cumulative}</div>
                        <span className={`inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded ${cumulative >= r.order_qty ? "bg-status-passed/15 text-status-passed" : cumulative > 0 ? "bg-amber-100 text-amber-700" : "bg-taupe/15 text-wmuted"}`}>
                          {status}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="hidden overflow-x-auto border border-taupe/20 rounded-lg md:block">
              <table className="w-full text-sm">
                <thead><tr className="text-wmuted text-left border-b border-taupe/20 bg-ivory/40">
                  <th className="py-2 px-3 font-medium">Produk</th><th className="py-2 px-3 font-medium">Varian</th><th className="py-2 px-3 text-right font-medium">Target</th><th className="py-2 px-3 text-right font-medium">Sebelumnya</th><th className="py-2 px-3 text-right font-medium">Sisa</th><th className="py-2 px-3 text-center font-medium">Diterima Hari Ini</th><th className="py-2 px-3 text-right font-medium">Total Setelah</th><th className="py-2 px-3 font-medium">Status</th>
                </tr></thead>
                <tbody>
                  {rows.map((r) => { const today = parseInt(r.today) || 0; const cum = r.previous_received_qty + today; const st = orderStatusOf(cum, r.order_qty); return (
                    <tr key={r.variant_id || r.size} className="border-b border-taupe/10" data-testid={`s1-row-${r.variant_id}`}>
                      <td className="py-2 px-3 text-cocoa" data-testid={`s1-product-${r.variant_id}`}>{r.product_name}</td>
                      <td className="py-2 px-3"><div className="flex items-center gap-2"><span className="w-4 h-4 rounded bg-taupe/40" /><span className="font-medium text-cocoa">{r.color} / {r.size}</span>{!r.variant_exists && <span className="text-[10px] text-status-reject">(varian belum ada)</span>}</div></td>
                      <td className="py-2 px-3 text-right">{r.order_qty}</td>
                      <td className="py-2 px-3 text-right text-wmuted">{r.previous_received_qty}</td>
                      <td className="py-2 px-3 text-right">{r.remaining_qty}</td>
                      <td className="py-2 px-3"><Input type="number" min="0" value={r.today} onChange={(e) => setRow(r.variant_id, { today: e.target.value })} className={`h-9 w-20 mx-auto text-center ${today > r.remaining_qty ? "border-amber-400" : ""}`} data-testid={`s1-today-${r.variant_id}`} /></td>
                      <td className="py-2 px-3 text-right font-semibold text-cocoa">{cum}</td>
                      <td className="py-2 px-3"><span className={`text-[11px] px-1.5 py-0.5 rounded ${cum >= r.order_qty ? "bg-status-passed/15 text-status-passed" : cum > 0 ? "bg-amber-100 text-amber-700" : "bg-taupe/15 text-wmuted"}`}>{st}</span></td>
                    </tr>
                  ); })}
                </tbody>
              </table>
            </div>
            {rows.some((r) => (parseInt(r.today) || 0) > r.remaining_qty) && <div className="mt-2" data-testid="override-box"><Label className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />Ada penerimaan melebihi sisa — wajib alasan override</Label><Input value={override} onChange={(e) => setOverride(e.target.value)} placeholder="Alasan over-receive..." className="mt-1" data-testid="override-reason" /></div>}
          </div>}

          {/* STEP 2 */}
          {step === 2 && <div data-testid="step-2">
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
              <div className="font-heading font-bold text-cocoa">Atur Harga Jahit per Varian</div>
              <div className="flex items-center gap-2">
                <div className="relative"><Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-wmuted" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari varian" className="h-8 pl-7 w-40" data-testid="s2-search" /></div>
                <label className="flex items-center gap-1.5 text-xs text-wmuted cursor-pointer"><input type="checkbox" checked={onlyChanged} onChange={(e) => setOnlyChanged(e.target.checked)} className="accent-cocoa" data-testid="s2-only-changed" />Hanya yang berubah</label>
              </div>
            </div>
            <div className="bg-ivory/50 border border-taupe/25 rounded-lg p-3 flex flex-wrap items-center gap-3 mb-3">
              <div className="flex items-center gap-2 text-sm font-medium text-cocoa"><Layers className="w-4 h-4" />Samakan Harga Jahit (opsional)</div>
              <div className="flex items-center gap-1"><span className="text-xs text-wmuted">Rp</span><Input type="number" value={bulk} onChange={(e) => setBulk(e.target.value)} className="h-9 w-32" data-testid="s2-bulk-price" /></div>
              <Button size="sm" className="bg-cocoa text-ivory" onClick={applyBulk} data-testid="s2-apply-all">Terapkan ke Semua Varian</Button>
              <label className="flex items-center gap-1.5 text-xs text-wmuted cursor-pointer"><input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} className="accent-cocoa" data-testid="s2-overwrite" />Timpa harga yang sudah pernah diisi</label>
              <span className="text-[11px] text-amber-700 flex items-center gap-1 ml-auto"><AlertTriangle className="w-3.5 h-3.5" />Harga per varian tetap dapat diedit setelah diterapkan.</span>
            </div>
            <div className="overflow-x-auto border border-taupe/20 rounded-lg">
              <table className="w-full text-sm">
                <thead><tr className="text-wmuted text-left border-b border-taupe/20 bg-ivory/40"><th className="py-2 px-3 font-medium">Varian</th><th className="py-2 px-3 text-right font-medium">Diterima Hari Ini</th><th className="py-2 px-3 text-right font-medium">Harga Sebelumnya</th><th className="py-2 px-3 text-center font-medium">Harga Jahit Baru</th><th className="py-2 px-3 text-right font-medium">Perubahan</th><th className="py-2 px-3 font-medium">Status</th><th></th></tr></thead>
                <tbody>
                  {activeRows.filter((r) => (`${r.color} ${r.size}`).toLowerCase().includes(q.toLowerCase())).filter((r) => !onlyChanged || (parseFloat(r.sewing_new) || 0) !== r.sewing_price_previous).map((r) => { const diff = (parseFloat(r.sewing_new) || 0) - r.sewing_price_previous; return (
                    <tr key={r.variant_id} className="border-b border-taupe/10" data-testid={`s2-row-${r.variant_id}`}>
                      <td className="py-2 px-3 font-medium text-cocoa">{r.color} / {r.size}</td>
                      <td className="py-2 px-3 text-right">{parseInt(r.today) || 0} pcs</td>
                      <td className="py-2 px-3 text-right text-wmuted">{fmtIDR(r.sewing_price_previous)}</td>
                      <td className="py-2 px-3"><div className="flex items-center gap-1 justify-center"><span className="text-xs text-wmuted">Rp</span><Input type="number" value={r.sewing_new} onChange={(e) => setRow(r.variant_id, { sewing_new: e.target.value })} className="h-9 w-28 text-right" data-testid={`s2-price-${r.variant_id}`} /></div></td>
                      <td className={`py-2 px-3 text-right ${diff > 0 ? "text-amber-700" : diff < 0 ? "text-status-reject" : "text-wmuted"}`}>{diff === 0 ? "Rp0" : (diff > 0 ? "+" : "") + fmtIDR(diff)}</td>
                      <td className="py-2 px-3"><span className={`text-[11px] px-1.5 py-0.5 rounded ${diff !== 0 ? "border border-amber-300 text-amber-700" : "bg-status-passed/15 text-status-passed"}`}>{diff !== 0 ? "Diubah" : "Tetap"}</span></td>
                      <td className="py-2 px-3">{diff !== 0 && <button onClick={() => setRow(r.variant_id, { sewing_new: r.sewing_price_previous })} className="text-[11px] text-cocoa flex items-center gap-0.5" data-testid={`s2-reset-${r.variant_id}`}><RotateCcw className="w-3 h-3" />Kembalikan</button>}</td>
                    </tr>
                  ); })}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center gap-4 mt-3 text-xs">
              <span className="flex items-center gap-1 text-cocoa"><PackageCheck className="w-4 h-4" />{activeRows.length} varian</span>
              <span className="flex items-center gap-1 text-amber-700"><Tag className="w-4 h-4" />{activeRows.filter((r) => (parseFloat(r.sewing_new) || 0) !== r.sewing_price_previous).length} harga diubah</span>
              <span className="text-wmuted">Rata-rata harga jahit baru: <b className="text-cocoa">{fmtIDR(activeRows.length ? activeRows.reduce((s, r) => s + (parseFloat(r.sewing_new) || 0), 0) / activeRows.length : 0)}</b></span>
              <div className="flex-1 min-w-[200px]"><Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Catatan harga jahit (opsional)" className="h-9" data-testid="s2-notes" /></div>
            </div>
          </div>}

          {/* STEP 3 */}
          {step === 3 && <div data-testid="step-3">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <div><div className="font-heading font-bold text-cocoa">Review & Konfirmasi HPP per Varian</div><div className="text-[11px] text-wmuted">HPP = bahan &amp; aksesori (BOM) + harga jahit + biaya cutting/tambahan per pcs.</div></div>
              <div className="flex items-center gap-1.5 text-xs"><span className="text-wmuted">Biaya Cutting/Tambahan/pcs</span><span className="text-wmuted">Rp</span><Input type="number" value={additional} onChange={(e) => setAdditional(e.target.value)} className="h-8 w-24" data-testid="s3-additional" /></div>
            </div>
            {changedHpp.length > 0 && <div className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-800 mb-2 flex items-center gap-2" data-testid="s3-hpp-notif"><AlertTriangle className="w-4 h-4" />{changedHpp.length} varian mengalami perubahan HPP.</div>}
            {anyBomIncomplete && <div className="rounded-lg border border-status-reject/30 bg-status-reject/10 p-2.5 text-xs text-status-reject mb-2 flex items-center gap-2" data-testid="s3-bom-warning"><AlertTriangle className="w-4 h-4" />Sebagian varian BOM Belum Lengkap — atur BOM dulu sebelum update HPP. <button className="underline" onClick={async () => { await saveDraft(true); window.open('/production/bom', '_blank'); }} data-testid="s3-set-bom">Atur BOM</button></div>}
            <div className="overflow-x-auto border border-taupe/20 rounded-lg">
              <table className="w-full text-sm">
                <thead><tr className="text-wmuted text-left border-b border-taupe/20 bg-ivory/40"><th className="py-2 px-2"><input type="checkbox" checked={activeRows.length > 0 && activeRows.filter((r) => r.bom_complete).every((r) => r.update_hpp)} onChange={(e) => setRows((rs) => rs.map((r) => (parseInt(r.today) > 0 && r.bom_complete) ? { ...r, update_hpp: e.target.checked } : r))} className="accent-cocoa" data-testid="s3-select-all" /></th><th className="py-2 px-2 font-medium">Varian</th><th className="py-2 px-2 text-right font-medium">Hari Ini</th><th className="py-2 px-2 text-right font-medium">Bahan & Aksesori</th><th className="py-2 px-2 text-right font-medium">Harga Jahit</th><th className="py-2 px-2 text-right font-medium">Biaya Tambahan</th><th className="py-2 px-2 text-right font-medium">HPP Baru</th><th className="py-2 px-2 text-right font-medium">HPP Sebelumnya</th><th className="py-2 px-2 text-right font-medium">Perubahan</th><th className="py-2 px-2 font-medium">Status</th></tr></thead>
                <tbody>
                  {activeRows.map((r) => { const nh = calcHpp(r); const diff = nh - r.previous_hpp; const dpct = r.previous_hpp > 0 ? (diff / r.previous_hpp * 100) : 0; const stat = !r.bom_complete ? "BOM Belum Lengkap" : (Math.abs(diff) < 0.5 ? "Tetap" : "Berubah"); return (
                    <tr key={r.variant_id} className="border-b border-taupe/10" data-testid={`s3-row-${r.variant_id}`}>
                      <td className="py-2 px-2"><input type="checkbox" disabled={!r.bom_complete} checked={!!r.update_hpp} onChange={(e) => setRow(r.variant_id, { update_hpp: e.target.checked })} className="accent-cocoa" data-testid={`s3-check-${r.variant_id}`} /></td>
                      <td className="py-2 px-2 font-medium text-cocoa">{r.color} / {r.size}</td>
                      <td className="py-2 px-2 text-right">{parseInt(r.today) || 0}</td>
                      <td className="py-2 px-2 text-right">{r.bom_complete ? fmtIDR(r.material_accessory_cost) : "—"}</td>
                      <td className="py-2 px-2 text-right">{fmtIDR(r.sewing_new)}</td>
                      <td className="py-2 px-2 text-right">{fmtIDR(additional)}</td>
                      <td className="py-2 px-2 text-right font-semibold text-cocoa">{r.bom_complete ? fmtIDR(nh) : "—"}</td>
                      <td className="py-2 px-2 text-right text-wmuted">{fmtIDR(r.previous_hpp)}</td>
                      <td className={`py-2 px-2 text-right ${diff > 0 ? "text-amber-700" : diff < 0 ? "text-status-reject" : "text-wmuted"}`}>{!r.bom_complete ? "—" : (diff === 0 ? "Rp0 (0%)" : `${diff > 0 ? "+" : ""}${fmtIDR(diff)} (${dpct >= 0 ? "+" : ""}${dpct.toFixed(1)}%)`)}</td>
                      <td className="py-2 px-2"><span className={`text-[11px] px-1.5 py-0.5 rounded ${stat === "Berubah" ? "border border-amber-300 text-amber-700" : stat === "Tetap" ? "bg-status-passed/15 text-status-passed" : "bg-status-reject/15 text-status-reject"}`}>{stat}</span></td>
                    </tr>
                  ); })}
                </tbody>
              </table>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 mt-3 flex flex-wrap gap-3 items-center">
              <div className="flex items-center gap-2"><Label className="text-xs">Alasan Perubahan HPP</Label>
                <Select value={reason} onValueChange={setReason}><SelectTrigger className="h-8 w-52" data-testid="s3-reason"><SelectValue placeholder="Pilih alasan" /></SelectTrigger><SelectContent><SelectItem value="Perubahan harga jahit">Perubahan harga jahit</SelectItem><SelectItem value="Perubahan harga bahan">Perubahan harga bahan</SelectItem><SelectItem value="Penyesuaian biaya tambahan">Penyesuaian biaya tambahan</SelectItem><SelectItem value="Koreksi HPP">Koreksi HPP</SelectItem></SelectContent></Select></div>
            </div>
            <label className="flex items-center gap-2 mt-3 cursor-pointer" data-testid="s3-approve-label"><input type="checkbox" checked={approved} onChange={(e) => setApproved(e.target.checked)} className="accent-cocoa w-4 h-4" data-testid="s3-approve" /><span className="text-sm font-medium text-cocoa">Saya sudah memeriksa dan menyetujui HPP di atas.</span></label>
          </div>}

          {/* STEP 4 */}
          {step === 4 && <div data-testid="step-4" className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2 border border-taupe/25 rounded-lg p-4 space-y-2">
              <div className="font-heading font-bold text-cocoa">Review Final Sebelum Posting</div>
              <div className="grid grid-cols-2 gap-2 text-sm items-center">
                <div className="text-wmuted">Gudang Tujuan</div>
                <div>
                  <Select value={wh} onValueChange={setWh}>
                    <SelectTrigger className="h-9 bg-white" data-testid="wizard-warehouse-select"><SelectValue placeholder="Pilih gudang" /></SelectTrigger>
                    <SelectContent>
                      {ctx.warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}{w.sales_bucket_tag ? ` (${w.sales_bucket_tag === "HOME" ? "Home" : "Store"})` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-wmuted">Purchase Batch</div><div className="text-cocoa font-medium text-right">{ctx.purchase_batch?.purchase_number || "(baru)"}</div>
                <div className="text-wmuted">Penerimaan ke-</div><div className="text-cocoa font-medium text-right">{ctx.next_sequence}</div>
                <div className="text-wmuted">Diterima Hari Ini</div><div className="text-status-passed font-bold text-right">{todayTotal} pcs</div>
                <div className="text-wmuted">Total Kumulatif Setelah</div><div className="text-cocoa font-medium text-right">{cumTotal} pcs</div>
                <div className="text-wmuted">Sisa Order</div><div className="text-cocoa font-medium text-right">{remainingOrder} pcs</div>
                <div className="text-wmuted">Status Setelah Posting</div><div className="text-right"><span className="text-[11px] px-2 py-0.5 rounded-full border border-amber-300 text-amber-700">{orderStatusOf(cumTotal, totalTarget)}</span></div>
              </div>
              <div className="pt-2 border-t border-taupe/15">
                <div className="text-xs font-semibold text-cocoa mb-1">Stok yang akan masuk</div>
                {activeRows.map((r) => <div key={r.variant_id} className="flex justify-between text-xs py-0.5"><span className="text-wmuted">{r.product_name} · {r.color} / {r.size}</span><span className="text-cocoa">{parseInt(r.today) || 0} pcs{r.update_hpp && r.bom_complete ? ` · HPP → ${fmtIDR(calcHpp(r))}` : ""}</span></div>)}
              </div>
            </div>
            <div className="border border-taupe/25 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm"><ShoppingCart className="w-4 h-4 text-cocoa" /><div><div className="text-[11px] text-wmuted">Update HPP</div><div className="font-bold text-cocoa">{changedHpp.length} varian</div></div></div>
              <div className="flex items-center gap-2 text-sm"><Warehouse className="w-4 h-4 text-cocoa" /><div><div className="text-[11px] text-wmuted">Total kumulatif diterima</div><div className="font-bold text-cocoa">{cumTotal} pcs</div></div></div>
              {receipts.filter((r) => r.status === "POSTED").length > 0 && <div className="pt-2 border-t border-taupe/15"><div className="text-xs font-semibold text-cocoa mb-1">Riwayat Penerimaan Batch</div>{receipts.filter((r) => r.status === "POSTED").map((r) => <div key={r.id} className="flex justify-between text-[11px] py-0.5" data-testid={`hist-${r.id}`}><span className="text-wmuted">{r.receipt_number} (ke-{r.receipt_sequence})</span><span className="text-cocoa">{r.total_received} pcs</span></div>)}</div>}
            </div>
            {remainingOrder > 0 && <div className="md:col-span-3 border border-amber-300 bg-amber-50 rounded-lg p-3"><label className="flex items-center gap-2 text-sm font-semibold text-amber-900"><input type="checkbox" checked={finalWithDifference} onChange={(e) => setFinalWithDifference(e.target.checked)} data-testid="final-difference-check" />Final dengan Selisih</label>{finalWithDifference && <Input value={finalDifferenceReason} onChange={(e) => setFinalDifferenceReason(e.target.value)} placeholder="Alasan selisih: cacat, hilang, produksi kurang..." className="mt-2 bg-white" data-testid="final-difference-reason" />}</div>}
          </div>}
          </>)}
        </div>

        {!posted && <DialogFooter className="sticky bottom-0 bg-white border-t border-taupe/20 px-6 py-3 flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 mr-auto text-xs text-wmuted"><PackageCheck className="w-4 h-4" />Penerimaan ke-{ctx.next_sequence} • {ctx.purchase_batch?.purchase_number || "batch baru"}{simple && <span className="text-status-passed">• Mode Ringkas (harga jahit & HPP pakai data sebelumnya)</span>}</div>
          {step > 1 && <Button variant="outline" onClick={() => setStep(simple && step === 4 ? 1 : step - 1)} className="border-taupe text-cocoa" data-testid="wizard-back">Kembali</Button>}
          <Button variant="outline" onClick={() => saveDraft(false)} className="border-cocoa text-cocoa" data-testid="wizard-save-draft">Simpan Draft</Button>
          {step === 1 && simple && <>
            <Button onClick={goPosting} className="bg-cocoa text-ivory" data-testid="wizard-next-posting">Confirm & Post Stock <ArrowRight className="w-4 h-4 ml-1" /></Button>
          </>}
          {step === 1 && !simple && <Button onClick={() => goStep(2)} className="bg-cocoa text-ivory" data-testid="wizard-next-1">Lanjut Harga Jahit <ArrowRight className="w-4 h-4 ml-1" /></Button>}
          {step === 2 && <Button onClick={() => goStep(3)} className="bg-cocoa text-ivory" data-testid="wizard-next-2">Lanjut Review HPP <ArrowRight className="w-4 h-4 ml-1" /></Button>}
          {step === 3 && <Button onClick={() => { if (changedHpp.length > 0 && !approved) return toast.error("Centang persetujuan HPP dulu"); goStep(4); }} className="bg-cocoa text-ivory" data-testid="wizard-next-3">Lanjut Posting Stok <ArrowRight className="w-4 h-4 ml-1" /></Button>}
          {step === 4 && <Button onClick={() => { if (finalWithDifference && !finalDifferenceReason.trim()) return toast.error("Alasan selisih wajib diisi"); setConfirm(true); }} disabled={busy} className="bg-status-passed text-white" data-testid="wizard-post">{finalWithDifference ? "Finalkan dengan Selisih" : "Posting Stok & Purchase"}</Button>}
        </DialogFooter>}

        {confirm && <Dialog open onOpenChange={() => setConfirm(false)}><DialogContent data-testid="post-confirm"><DialogHeader><DialogTitle className="text-cocoa">Konfirmasi Posting</DialogTitle><DialogDescription>Posting akan menambahkan {todayTotal} pcs ke stok {(ctx.warehouses.find((w) => w.id === wh) || {}).name}, memperbarui Purchase Batch {ctx.purchase_batch?.purchase_number || "(baru)"}, dan memperbarui HPP {changedHpp.length} varian. Lanjutkan?</DialogDescription></DialogHeader><DialogFooter><Button variant="outline" onClick={() => setConfirm(false)} className="border-taupe text-cocoa">Batal</Button><Button onClick={doPost} disabled={busy} className="bg-status-passed text-white" data-testid="post-confirm-btn">{busy ? "Memposting..." : "Ya, Posting"}</Button></DialogFooter></DialogContent></Dialog>}
      </DialogContent>
    </Dialog>
  );
}
